import useSurveyLocale from '@aytm/app/swidget/base/modules/locale/useSurveyLocale'
import useTimeline from '@aytm/app/swidget/base/modules/timeline/useTimeline'
import useViewport from '@aytm/app/swidget/base/modules/viewport/useViewport.ts'
import EmptyLink from '@aytm/app/swidget/base/components/EmptyLink'
import { getSmallAssetPath } from '@aytm/lib/common/assets'
import {
  EcommerceBannerPlacement,
  EcommerceResponse,
  IEcommerceAnswerSchema,
  IEcommerceQuestionSchema
} from '../../types.ts'
import { formatPriceWithLocale } from '../../utils/formatPriceWithLocale.ts'
import { readDimensions } from '../../utils/familyAxes.ts'
import AddToCartButton from './AddToCartButton.tsx'
import BannerCard from './BannerCard.tsx'
import Breadcrumbs from './Breadcrumbs.tsx'
import emptyCartIcon from '../assets/icons/emptyCart.svg'
import shippingIcon from '../assets/icons/shipping.svg'
import cardsImage from '../assets/images/cards.jpg'
import style from './Cart.module.scss'

interface ICartProps {
  schema: IEcommerceQuestionSchema
  response: EcommerceResponse
  totals: number[]
  findability: boolean
  onCartClose: () => void
  onItemSelect: (index: number, bannerPlacement?: EcommerceBannerPlacement) => void
  onAddToCart: (index: number, qnt: number, inCarousel?: boolean) => void
  onDeleteFromCart: (index: number) => void
  onUpdateResponse: (response: EcommerceResponse) => void
  checkout: () => void
}

const Cart = ({
  schema,
  response,
  totals,
  findability,
  onCartClose,
  onItemSelect,
  onAddToCart,
  onDeleteFromCart,
  onUpdateResponse,
  checkout
}: ICartProps) => {
  const { t, languageTag } = useSurveyLocale()
  const timeline = useTimeline()
  const viewport = useViewport()
  const na = schema.answers.find(answer => answer.na)
  const itemCount = totals[0]
  const estimatedTotal = totals[1]

  const price = (value: number) => formatPriceWithLocale(value, schema.price_mask, languageTag, true, false)
  const hasDiscount = (answer: IEcommerceAnswerSchema) =>
    Boolean(answer.discounted_price) && answer.price > answer.discounted_price

  // Cart lines show line totals (price x qty) — the Walmart-family convention.
  const lineOff = (answer: IEcommerceAnswerSchema, qty: number) => {
    const diff = (answer.price - answer.discounted_price) * qty
    return schema.price_mask.replace('{{price}}', diff.toFixed(2))
  }

  const cartItems = schema.answers.filter(answer => !answer.na && response.answers[answer.index].v > 0)

  // Variants of one product share the title — the dimension values are what tells lines apart.
  const dimensionsOf = (answer: IEcommerceAnswerSchema) =>
    readDimensions(answer)
      .map(dimension => dimension.value)
      .filter(Boolean)
      .join(', ')

  // "Items you may like" = featured top-level products not yet in the cart.
  const suggestions = schema.answers.filter(
    answer => answer.featured && !answer.na && !answer.parent_product && !response.answers[answer.index].v
  )

  // Subtotal is the pre-discount sum; savings is what the discounts remove; the estimated
  // total is the money total (post-discount) the shell already computes.
  const subtotal = schema.answers.reduce((sum, a) => sum + (a.na ? 0 : response.answers[a.index].v * a.price), 0)
  const savings = Math.max(0, subtotal - estimatedTotal)

  // Decorative shipping ETA: always today + 3 days (no randomness) — same as the PDP.
  const shippingDate = new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toLocaleDateString(languageTag, {
    weekday: 'short',
    month: 'short',
    day: 'numeric'
  })

  // Cart caps are advisory while shopping and only bite at checkout — the shared behaviour
  // of every other retailer. The message carries a price mask, so it is markup.
  const valid =
    (schema.max_money_spent ? estimatedTotal <= schema.max_money_spent : true) &&
    (schema.max_cart_size ? itemCount <= schema.max_cart_size : true)
  const cartError = [
    schema.max_money_spent && estimatedTotal > schema.max_money_spent
      ? t<(max: string) => string>('questions', 'ec', 'maxSpentError')(price(schema.max_money_spent))
      : '',
    schema.max_cart_size && itemCount > schema.max_cart_size
      ? t<(max: number) => string>('questions', 'ec', 'maxCartCountError')(schema.max_cart_size)
      : ''
  ]
    .filter(Boolean)
    .join('')

  // "None of these" zeroes every other answer; the shell's na effect then finishes the question.
  const onNaSelect = () => {
    if (!na) return
    timeline.log({ type: 'na_select', ref: na.id })
    response.answers = response.answers.map(answer => ({ ...answer, v: answer.id === na.id ? 1 : 0 }))
    onUpdateResponse(response)
  }

  const suggestCard = suggestions.length > 0 && (
    <div className={style.suggestCard}>
      <h2 className={style.suggestTitle}>{t('questions', 'ec', 'samsclub', 'itemsYouMayLike')}</h2>
      <div className={style.suggestRow} data-testid="SW-EC-ItemsYouMayLike">
        {suggestions.map(answer => (
          <div className={style.suggestItem} key={answer.id}>
            <BannerCard
              schema={schema}
              answer={answer}
              amount={response.answers[answer.index].v}
              findability={findability}
              disabled={'focus' == schema.mode_type && !answer.focused}
              variant="recommendation"
              onSelect={() => onItemSelect(answer.index, 'featured')}
              onAdd={qnt => onAddToCart(answer.index, qnt, true)}
            />
          </div>
        ))}
      </div>
    </div>
  )

  const lineThumb = (answer: IEcommerceAnswerSchema) =>
    answer.image ? (
      <img
        className={style.lineThumb}
        alt={answer.image?.alt_text || t('questions', 'img', 'noAlt')}
        src={getSmallAssetPath(answer.image)}
        onClick={() => onItemSelect(answer.index)}
      />
    ) : (
      <div className={style.lineNoImg} onClick={() => onItemSelect(answer.index)} />
    )

  const lineTitle = (answer: IEcommerceAnswerSchema) => (
    <span
      className={style.lineTitle}
      onClick={() => onItemSelect(answer.index)}
      dangerouslySetInnerHTML={{ __html: answer.text }}
    />
  )

  // Line total, struck full price and the "$N off" line; the offers link is desktop-only.
  const linePriceBlock = (answer: IEcommerceAnswerSchema, qty: number, offers: boolean) => (
    <div className={style.linePrice}>
      {hasDiscount(answer) ? (
        <>
          <div className={style.priceRow}>
            <span
              className={style.priceSale}
              dangerouslySetInnerHTML={{ __html: price(qty * answer.discounted_price) }}
            />
            <span className={style.priceOld} dangerouslySetInnerHTML={{ __html: price(qty * answer.price) }} />
          </div>
          <span className={style.saveOff}>
            {t<(amount: string) => string>('questions', 'ec', 'samsclub', 'saveOff')(lineOff(answer, qty))}
          </span>
          {offers && (
            <EmptyLink color="#0a6cff">
              <span className={style.actionLink}>{t('questions', 'ec', 'samsclub', 'seeOffers')}</span>
            </EmptyLink>
          )}
        </>
      ) : (
        <span className={style.price} dangerouslySetInnerHTML={{ __html: price(qty * answer.price) }} />
      )}
    </div>
  )

  const lineStepper = (answer: IEcommerceAnswerSchema, qty: number) => (
    <AddToCartButton
      amount={qty}
      findability={false}
      limit={schema.max_qty_per_product}
      placement="cart"
      onAdd={qnt => onAddToCart(answer.index, qnt)}
    />
  )

  const removeButton = (answer: IEcommerceAnswerSchema) => (
    <button type="button" className={style.removeLink} onClick={() => onDeleteFromCart(answer.index)}>
      {t('questions', 'ec', 'samsclub', 'remove')}
    </button>
  )

  // Narrow line: thumb / title + unit price / line total, then the stepper beside the app's
  // inert "..." control. No Remove here — stepping down to zero drops the line.
  const renderMobileLine = (answer: IEcommerceAnswerSchema) => {
    const qty = response.answers[answer.index].v
    const unitPrice = hasDiscount(answer) ? answer.discounted_price : answer.price
    const dimensions = dimensionsOf(answer)
    return (
      <div className={style.lineMobile} key={answer.id} data-testid="SW-EC-CartItem">
        <div className={style.lineMobileBody}>
          {lineThumb(answer)}
          <div className={style.lineInfo}>
            {lineTitle(answer)}
            {dimensions && <span className={style.lineDimensions}>{dimensions}</span>}
            <span
              className={style.lineUnit}
              dangerouslySetInnerHTML={{
                __html: t<(price: string) => string>('questions', 'ec', 'samsclub', 'perEach')(price(unitPrice))
              }}
            />
          </div>
          {linePriceBlock(answer, qty, false)}
        </div>
        <div className={style.lineMobileControls}>
          {lineStepper(answer, qty)}
          <EmptyLink color="#0a6cff">
            <span className={style.moreButton} role="img" aria-label={t('questions', 'ec', 'samsclub', 'moreActions')}>
              <i className="ld-More" />
            </span>
          </EmptyLink>
        </div>
      </div>
    )
  }

  const renderLine = (answer: IEcommerceAnswerSchema) => {
    const qty = response.answers[answer.index].v
    const dimensions = dimensionsOf(answer)
    return (
      <div className={style.line} key={answer.id} data-testid="SW-EC-CartItem">
        <div className={style.lineBody}>
          {lineThumb(answer)}
          <div className={style.lineInfo}>
            {lineTitle(answer)}
            {dimensions && <span className={style.lineDimensions}>{dimensions}</span>}
          </div>
          {linePriceBlock(answer, qty, true)}
        </div>
        <div className={style.lineActions}>
          {removeButton(answer)}
          <EmptyLink color="#0a6cff">
            <span className={style.actionLink}>{t('questions', 'ec', 'samsclub', 'saveForLater')}</span>
          </EmptyLink>
          <EmptyLink color="#0a6cff">
            <span className={style.actionLink}>{t('questions', 'ec', 'samsclub', 'deliverInstead')}</span>
          </EmptyLink>
          <EmptyLink color="#0a6cff">
            <span className={style.actionLink}>{t('questions', 'ec', 'samsclub', 'pickupInstead')}</span>
          </EmptyLink>
          <div className={style.lineStepper}>{lineStepper(answer, qty)}</div>
        </div>
      </div>
    )
  }

  const emptyAside = (
    <div className={style.emptyAside}>
      <img src={emptyCartIcon} alt="" role="presentation" className={style.emptyIcon} />
      <div className={style.emptyTitle}>{t('questions', 'ec', 'samsclub', 'fullCartHappy')}</div>
      <button type="button" className={style.continueShopping} onClick={onCartClose}>
        {t('questions', 'ec', 'samsclub', 'continueShopping')}
      </button>
      {/* The only place the respondent can pick "None of these" — mirrors Kroger / Costco. */}
      {na && (
        <button type="button" className={style.naButton} onClick={onNaSelect}>
          {na.text || t('questions', 'ec', 'na')}
        </button>
      )}
    </div>
  )

  const summaryRows = (
    <div className={style.summary}>
      <div className={style.summaryRow}>
        <span>
          {t('questions', 'ec', 'samsclub', 'subtotal')}{' '}
          {t<(count: number) => string>('questions', 'ec', 'samsclub', 'cartItemsCount')(itemCount)}
        </span>
        <span className={savings > 0 ? style.subtotalOld : ''} dangerouslySetInnerHTML={{ __html: price(subtotal) }} />
      </div>
      {savings > 0 && (
        <>
          <div className={`${style.summaryRow} ${style.savingsRow}`}>
            <span>{t('questions', 'ec', 'samsclub', 'memberSavings')}</span>
            <span>
              &minus;
              <span dangerouslySetInnerHTML={{ __html: price(savings) }} />
            </span>
          </div>
          <div className={style.summaryRow}>
            <span />
            <span dangerouslySetInnerHTML={{ __html: price(estimatedTotal) }} />
          </div>
        </>
      )}
      <div className={style.summaryDivider} />
      <div className={style.summaryRow}>
        <span className={style.summaryNote}>
          {t('questions', 'ec', 'samsclub', 'shippingCosts')}
          <EmptyLink color="#0a6cff">
            <i className="ld-InfoCircle" />
          </EmptyLink>
        </span>
        <span className={style.freeValue}>{t('questions', 'ec', 'samsclub', 'free')}</span>
      </div>
      <div className={style.summaryRow}>
        <span>{t('questions', 'ec', 'samsclub', 'taxCalculated')}</span>
        <span className={style.tbd}>--</span>
      </div>
      <div className={`${style.summaryRow} ${style.totalRow}`}>
        <span>{t('questions', 'ec', 'samsclub', 'estTotal')}</span>
        <span dangerouslySetInnerHTML={{ __html: price(estimatedTotal) }} />
      </div>
    </div>
  )

  const creditOffer = (
    <div className={style.creditCard}>
      <img src={cardsImage} alt="" role="presentation" className={style.creditImage} />
      <div className={style.creditText}>
        <span>{t('questions', 'ec', 'samsclub', 'creditOffer')}</span>
        <EmptyLink color="#0a6cff">
          <span className={style.applyNow}>{t('questions', 'ec', 'samsclub', 'applyNow')}</span>
        </EmptyLink>
      </div>
    </div>
  )

  if (viewport.mobile) {
    return (
      <section className={`${style.cart} ${style.cartMobile}`} data-testid="SW-EC-Cart">
        {0 === itemCount ? (
          <div className={`${style.emptyLayout} ${style.emptyLayoutMobile}`}>{emptyAside}</div>
        ) : (
          <>
            <div className={style.shippingCard}>
              <div className={`${style.shippingHead} ${style.shippingHeadMobile}`}>
                <img src={shippingIcon} alt="" role="presentation" className={style.shippingIcon} />
                <div>
                  <h2 className={style.shippingTitle}>{t('questions', 'ec', 'samsclub', 'shippingTitle')}</h2>
                  <div className={style.shippingEta}>
                    {t<(date: string) => string>('questions', 'ec', 'samsclub', 'arrivesBy')(shippingDate)}
                  </div>
                  <EmptyLink color="#0a6cff">
                    <span className={style.shippingZip}>{t('questions', 'ec', 'samsclub', 'cartZip')}</span>
                  </EmptyLink>
                </div>
              </div>
              <div className={`${style.lines} ${style.linesMobile}`}>{cartItems.map(renderMobileLine)}</div>
            </div>

            {suggestCard}

            <div className={`${style.summaryCard} ${style.summaryCardMobile}`}>{summaryRows}</div>

            {creditOffer}

            {/* The summary is a bar pinned to the bottom, in the slot the tab bar gives up while the cart is open. */}
            <div className={style.stickyBar} data-testid="SW-EC-Cart-CheckoutBar">
              {!valid && <div className={style.cartError} dangerouslySetInnerHTML={{ __html: cartError }} />}
              <div className={style.stickyTotalRow}>
                <span>
                  {t('questions', 'ec', 'samsclub', 'estTotal')}{' '}
                  <span className={style.titleCount}>
                    {t<(count: number) => string>('questions', 'ec', 'samsclub', 'cartItemsCount')(itemCount)}
                  </span>
                </span>
                <span dangerouslySetInnerHTML={{ __html: price(estimatedTotal) }} />
              </div>
              <button type="button" className={style.checkout} onClick={checkout} disabled={!valid}>
                {t('questions', 'ec', 'samsclub', 'checkout')}
              </button>
              <div className={style.stickyTerms}>
                <EmptyLink color="#0a6cff">
                  <span className={style.stickyTermsLink}>{t('questions', 'ec', 'samsclub', 'termsConditions')}</span>
                </EmptyLink>
              </div>
            </div>
          </>
        )}
      </section>
    )
  }

  return (
    <section className={style.cart} data-testid="SW-EC-Cart">
      <Breadcrumbs schema={schema} current={t('questions', 'ec', 'samsclub', 'cart')} onBack={onCartClose} />
      <h1 className={style.title}>
        {t('questions', 'ec', 'samsclub', 'cart')}{' '}
        <span className={style.titleCount}>
          {t<(count: number) => string>('questions', 'ec', 'samsclub', 'cartItemsCount')(itemCount)}
        </span>
      </h1>

      {0 === itemCount ? (
        <div className={style.emptyLayout}>
          {suggestCard}
          {emptyAside}
        </div>
      ) : (
        <div className={style.columns}>
          <div className={style.main}>
            <div className={style.shippingCard}>
              {/* One shipping group for every line — the simulator has a single fulfilment. */}
              <div className={style.shippingHead}>
                <img src={shippingIcon} alt="" role="presentation" className={style.shippingIcon} />
                <div>
                  <h2 className={style.shippingTitle}>
                    {t<(date: string) => string>('questions', 'ec', 'samsclub', 'shippingArrives')(shippingDate)}
                  </h2>
                  <EmptyLink color="#0a6cff">
                    <span className={style.shippingZip}>{t('questions', 'ec', 'samsclub', 'cartZip')}</span>
                  </EmptyLink>
                </div>
              </div>
              <div className={style.lines}>
                {itemCount > 1 && (
                  <div className={style.linesHead}>
                    <span className={style.linesCount}>
                      {t<(count: number) => string>('questions', 'ec', 'samsclub', 'itemsCount')(itemCount)}
                    </span>
                    <EmptyLink color="#0a6cff">
                      <span className={style.actionLink}>{t('questions', 'ec', 'samsclub', 'hideDetails')}</span>
                    </EmptyLink>
                  </div>
                )}
                {cartItems.map(renderLine)}
              </div>
            </div>

            {suggestCard}
          </div>

          <aside className={style.aside}>
            <div className={style.summaryCard}>
              <button type="button" className={style.checkout} onClick={checkout} disabled={!valid}>
                {t('questions', 'ec', 'samsclub', 'checkout')}
              </button>
              <div className={style.termsLine}>
                <EmptyLink color="#0a6cff">
                  <span className={style.underlineLink}>{t('questions', 'ec', 'samsclub', 'termsConditions')}</span>
                </EmptyLink>
              </div>

              {!valid && <div className={style.cartError} dangerouslySetInnerHTML={{ __html: cartError }} />}

              {summaryRows}
            </div>

            {creditOffer}
          </aside>
        </div>
      )}
    </section>
  )
}

export default Cart
